import { useState } from 'react';
import { useBrightSaaSTooltips } from '../hooks/useBrightSaaSTooltips';
import { useModal } from '../context/ModalContext';
import { pricingPlans } from 'src/features/MP/components/templates/PricingPage/pricings';

const PLAN_NAMES = [
  'DIGITAL BASELINE',
  'DIGITAL ADVANCED',
  'CROSS-CHANNEL GROWTH',
  'FULL-SCALE ENTERPRISE',
] as const;

export function PricingPage() {
  useBrightSaaSTooltips();
  const { open } = useModal();
  const startModeling = () =>
    open('contact', {
      title: 'Start Modeling',
      submitText: 'Submit Application',
    });
  const [yearly, setYearly] = useState(false);
  const priceClass = yearly
    ? 'pricing-price mb-8 yearly-active'
    : 'pricing-price mb-8';
  return (
    <>
      <style>{`
        .pricing-price .monthly-price,
        .pricing-price .yearly-price {
          transition: opacity .4s ease, transform .4s ease;
        }
        .pricing-price .monthly-price { opacity: 1; transform: translateY(0); }
        .pricing-price .yearly-price { opacity: 0; transform: translateY(30px); }
        .pricing-price.yearly-active .monthly-price {
          opacity: 0;
          transform: translateY(-30px);
          pointer-events: none;
        }
        .pricing-price.yearly-active .yearly-price {
          opacity: 1;
          transform: translateY(0);
          pointer-events: auto;
        }
      `}</style>
      <header className="nk-header" id="home">
        <div className="section section-lg section-b-0">
          <div className="section-heading">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-xxl-7 col-lg-8">
                  <div className="position-relative text-center">
                    <div className="fs-9 text-uppercase text-gradient-a w-max mx-auto mb-4">
                      Pricing
                    </div>
                    <h2 className="title text-gradient-heading fw-semibold display-6 mb-4">
                      Simple Plans, Powerful Results
                    </h2>
                    <p className="fs-8">
                      Choose the plan that fits your growth.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="nk-main">
        <div className="section section-xl section-t-0">
          <div className="section-content has-shape">
            <div className="bg-shape bg-glow-small-c ms-15p mt-12"></div>
            <div className="bg-shape bg-glow-small-c ms-n15p mt-n20"></div>
            <div className="container">
              <div className="mb-10 d-flex justify-content-center">
                <div
                  className={
                    yearly
                      ? 'pricing-switcher yearly-active'
                      : 'pricing-switcher'
                  }
                  style={{ pointerEvents: 'auto' }}>
                  <button
                    type="button"
                    onClick={() => setYearly(false)}
                    className="pricing-switcher-button monthly">
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setYearly(true)}
                    className="pricing-switcher-button yearly">
                    Yearly{' '}
                    <div className="ms-2 badge badge-xxs bg-gradient-c rounded-pill">
                      -20%
                    </div>
                  </button>
                </div>
              </div>
              <div className="card card-bordered">
                <div className="card-group row g-0">
                  <div className="col-xl-3 col-sm-6 card-body position-relative">
                    <div className="pricing-title">
                      <h5 className="fs-6">DIGITAL BASELINE</h5>
                      <p>
                        For digital marketers exploring what drives their
                        performance.
                      </p>
                    </div>
                    <div className={priceClass}>
                      <div className="monthly-price">
                        <div className="badge badge-xs bg-gradient-a text-background">
                          min period 3 months
                        </div>
                        <div className="d-flex align-items-end gap-2 mb-1">
                          <span className="price fs-1 fw-normal lh-1 text-title">
                            €490
                          </span>
                          <small className="period fs-8 text-content">
                            /month
                          </small>
                        </div>
                        <div className="fs-10 text-content mb-3">
                          Excludes VAT
                        </div>
                      </div>
                      <div className="yearly-price">
                        <div className="text-content">
                          <s>€5,880</s>
                        </div>
                        <div className="d-flex align-items-end gap-2 mb-1">
                          <span className="price fs-1 fw-normal lh-1 text-title">
                            €4,700
                          </span>
                          <small className="period fs-8 text-content">
                            /yearly
                          </small>
                        </div>
                        <div className="fs-10 text-content mb-3">
                          Excludes VAT
                        </div>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Media scope: Digital only
                        </p>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Active models: 3
                        </p>
                        <em
                          className="text-primary icon ni ni-info"
                          data-bs-toggle="tooltip"
                          data-bs-title="Run up to 3 independent analyses — by KPI, product category, or channel mix (additional €140/mo each)"></em>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Model refresh: Monthly
                        </p>
                        <em
                          className="text-primary icon ni ni-info"
                          data-bs-toggle="tooltip"
                          data-bs-title="each model"></em>
                      </div>
                    </div>
                    <div className="pricing-actions">
                      <button
                        type="button"
                        onClick={startModeling}
                        className="btn  btn-primary">
                        Start here
                      </button>
                    </div>
                  </div>
                  <div className="col-xl-3 col-sm-6 card-body position-relative">
                    <div className="pricing-title">
                      <h5 className="fs-6">DIGITAL ADVANCED</h5>
                      <p>
                        For results-focused marketers operating in digital
                        channels.
                      </p>
                    </div>
                    <div className={priceClass}>
                      <div className="monthly-price">
                        <div className="badge badge-xs bg-gradient-a text-background">
                          min period 3 months
                        </div>
                        <div className="d-flex align-items-end gap-2 mb-1">
                          <span className="price fs-1 fw-normal lh-1 text-title">
                            €990
                          </span>
                          <small className="period fs-8 text-content">
                            /month
                          </small>
                        </div>
                        <div className="fs-10 text-content mb-3">
                          Excludes VAT
                        </div>
                      </div>
                      <div className="yearly-price">
                        <div className="text-content">
                          <s>€11,880</s>
                        </div>
                        <div className="d-flex align-items-end gap-2 mb-1">
                          <span className="price fs-1 fw-normal lh-1 text-title">
                            €9,500
                          </span>
                          <small className="period fs-8 text-content">
                            /yearly
                          </small>
                        </div>
                        <div className="fs-10 text-content mb-3">
                          Excludes VAT
                        </div>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Media scope: Digital only
                        </p>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Active models: 3
                        </p>
                        <em
                          className="text-primary icon ni ni-info"
                          data-bs-toggle="tooltip"
                          data-bs-title="Run up to 3 independent analyses — by KPI, product category, or channel mix (additional €120/mo each)"></em>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Model refresh: Weekly
                        </p>
                        <em
                          className="text-primary icon ni ni-info"
                          data-bs-toggle="tooltip"
                          data-bs-title="each model"></em>
                      </div>
                    </div>
                    <div className="pricing-actions">
                      <button
                        type="button"
                        onClick={startModeling}
                        className="btn  btn-primary">
                        Start here
                      </button>
                    </div>
                  </div>
                  <div className="col-xl-3 col-sm-6 card-body position-relative">
                    <div className="pricing-title">
                      <h5 className="fs-6">CROSS-CHANNEL GROWTH</h5>
                      <p>
                        For brands combining digital and traditional media
                        across seasons.
                      </p>
                    </div>
                    <div className={priceClass}>
                      <div className="monthly-price">
                        <div className="badge badge-xs bg-gradient-a text-background">
                          min period 6 months
                        </div>
                        <div className="d-flex align-items-end gap-2 mb-1">
                          <span className="price fs-1 fw-normal lh-1 text-title">
                            €1,490
                          </span>
                          <small className="period fs-8 text-content">
                            /month
                          </small>
                        </div>
                        <div className="fs-10 text-content mb-3">
                          Excludes VAT
                        </div>
                      </div>
                      <div className="yearly-price">
                        <div className="text-content">
                          <s>€17,880</s>
                        </div>
                        <div className="d-flex align-items-end gap-2 mb-1">
                          <span className="price fs-1 fw-normal lh-1 text-title">
                            €14,300
                          </span>
                          <small className="period fs-8 text-content">
                            /yearly
                          </small>
                        </div>
                        <div className="fs-10 text-content mb-3">
                          Excludes VAT
                        </div>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Media scope: Digital and Offline
                        </p>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Active models: 5
                        </p>
                        <em
                          className="text-primary icon ni ni-info"
                          data-bs-toggle="tooltip"
                          data-bs-title="Run up to 5 independent analyses — by KPI, product category, or channel mix (additional €90/mo each)"></em>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Model refresh: Monthly
                        </p>
                        <em
                          className="text-primary icon ni ni-info"
                          data-bs-toggle="tooltip"
                          data-bs-title="each model"></em>
                      </div>
                    </div>
                    <div className="pricing-actions">
                      <button
                        type="button"
                        onClick={startModeling}
                        className="btn  btn-primary">
                        Start here
                      </button>
                    </div>
                  </div>
                  <div className="col-xl-3 col-sm-6 card-body position-relative">
                    <div className="pricing-title">
                      <h5 className="fs-6">FULL-SCALE ENTERPRISE</h5>
                      <p>
                        For high-volume advertisers running always-on,
                        multi-channel campaigns.
                      </p>
                    </div>
                    <div className={priceClass}>
                      <div className="monthly-price">
                        <div className="badge badge-xs bg-gradient-a text-background">
                          min period 6 months
                        </div>
                        <div className="d-flex align-items-end gap-2 mb-1">
                          <span className="price fs-1 fw-normal lh-1 text-title">
                            €1,990
                          </span>
                          <small className="period fs-8 text-content">
                            /month
                          </small>
                        </div>
                        <div className="fs-10 text-content mb-3">
                          Excludes VAT
                        </div>
                      </div>
                      <div className="yearly-price">
                        <div className="text-content">
                          <s>€23,880</s>
                        </div>
                        <div className="d-flex align-items-end gap-2 mb-1">
                          <span className="price fs-1 fw-normal lh-1 text-title">
                            €19,100
                          </span>
                          <small className="period fs-8 text-content">
                            /yearly
                          </small>
                        </div>
                        <div className="fs-10 text-content mb-3">
                          Excludes VAT
                        </div>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Media scope: Digital and Offline
                        </p>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Active models: 5
                        </p>
                        <em
                          className="text-primary icon ni ni-info"
                          data-bs-toggle="tooltip"
                          data-bs-title="Run up to 5 independent analyses — by KPI, product category, or channel mix (additional €70/mo each)"></em>
                      </div>
                      <div className="d-flex gap-1 align-items-center">
                        <p className="mb-0 text-title text-opacity-90">
                          Model refresh: Weekly
                        </p>
                        <em
                          className="text-primary icon ni ni-info"
                          data-bs-toggle="tooltip"
                          data-bs-title="each model"></em>
                      </div>
                    </div>
                    <div className="pricing-actions">
                      <button
                        type="button"
                        onClick={startModeling}
                        className="btn  btn-primary">
                        Start here
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="section-footer">
            <div className="container">
              <div className="text-gradient-a fs-7 w-max mb-4 ps-5 ps-md-8">
                Core Inclusions
              </div>
              <div className="row g-0">
                {PLAN_NAMES.map((plan) => (
                  <div key={plan} className="col-xl-3 col-sm-6">
                    <div className="card border-0 bg-transparent">
                      <div className="card-body pt-0">
                        <div className="pricing-title">
                          <h5 className="fs-8">{plan}</h5>
                        </div>
                        <div className="pricing-features">
                          <ul className="nk-list fs-8">
                            {Object.entries(
                              pricingPlans[plan].coreInclusions,
                            ).map(([feature, included]) => (
                              <li key={feature}>
                                {included ? (
                                  <em className="icon me-1 fs-9 lh-inherit text-gradient-a ni ni-check-circle-fill" />
                                ) : (
                                  <em className="icon me-1 fs-9 lh-inherit text-light text-opacity-50 ni ni-cross-circle-fill" />
                                )}{' '}
                                <span
                                  className={
                                    included ? '' : 'text-light text-opacity-50'
                                  }>
                                  {feature}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="container">
              <div className="text-gradient-a fs-7 w-max mb-4 ps-5 ps-md-8">
                Full Feature List
              </div>
              <div className="row g-0">
                {PLAN_NAMES.map((plan) => (
                  <div key={plan} className="col-xl-3 col-sm-6">
                    <div className="card border-0 bg-transparent">
                      <div className="card-body pt-0">
                        <div className="pricing-title">
                          <h5 className="fs-8">{plan}</h5>
                        </div>
                        <div className="pricing-features">
                          <ul className="nk-list fs-8">
                            {Object.entries(
                              pricingPlans[plan].fullFeatures,
                            ).map(([feature, included]) => (
                              <li key={feature}>
                                {included ? (
                                  <em className="icon me-1 fs-9 lh-inherit text-gradient-a ni ni-check-circle-fill" />
                                ) : (
                                  <em className="icon me-1 fs-9 lh-inherit text-light text-opacity-50 ni ni-cross-circle-fill" />
                                )}{' '}
                                <span
                                  className={
                                    included ? '' : 'text-light text-opacity-70'
                                  }>
                                  {feature}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
